import { useCallback, useEffect, useMemo, useState } from "react";
import {
  listarPromocionesDisponibles,
  listarPromocionesFinalizadas,
} from "../../../../services/promocionesService";
import { listarCursos } from "../../../../services/cursosService";
import { showToast } from "../../../../utils/showToast";

const buildPromocionLabel = (item = {}, { marcarFinalizada = false } = {}) => {
  const nombre = item.numero_promocion || item.nombre || `Promocion #${item.id}`;
  const curso = item.curso || item.curso_nombre || item.cursoNombre || "";

  const cuposRaw = item.cupos_disponibles ?? item.cuposDisponibles;
  const cupos = cuposRaw === null || cuposRaw === undefined ? null : Number(cuposRaw);
  const capacidad = item.capacidad;
  const capacidadValida = capacidad !== null && capacidad !== undefined && !Number.isNaN(Number(capacidad));
  const disponibilidad = capacidadValida
    ? cupos === null || Number.isNaN(cupos)
      ? "Cupos no definidos"
      : `${cupos} cupos libres`
    : null;

  const parts = [nombre, curso, disponibilidad].filter(Boolean);
  const label = parts.join(" | ");
  return marcarFinalizada ? `${label} (Finalizada)` : label;
};

const buildCiclosOptions = (items) => {
  const ciclosMap = new Map();
  (Array.isArray(items) ? items : []).forEach((item) => {
    const id = item.ciclo_id ?? item.cicloId;
    if (id && !ciclosMap.has(id)) {
      const centroAlias = item.centro_alias ?? "";
      const codigoMdi = item.centro_codigo_mdi ?? "";
      const anio = item.ciclo_anio ?? item.cicloAnio ?? "";
      const cicloCodigo = item.ciclo_codigo ?? item.cicloCodigo ?? "";
      const label = [anio, cicloCodigo, centroAlias, codigoMdi].filter(Boolean).join(" · ") || `Ciclo #${id}`;
      ciclosMap.set(id, { value: id, label });
    }
  });
  return [...ciclosMap.values()];
};

const buildPromocionesOptions = (items, labelOpts) =>
  (Array.isArray(items) ? items : []).map((item) => ({
    value: item.id,
    label: buildPromocionLabel(item, labelOpts),
    estado: item.estado,
    cursoId: item.curso_id ?? item.cursoId ?? null,
    cicloId: item.ciclo_id ?? item.cicloId ?? null,
    precioBase: Number(item.precio_base ?? 0),
    descuentoPorcentaje: Number(item.descuento_porcentaje ?? 0),
  }));

const mergeById = (base, extra) => {
  if (!extra.length) return base;
  const existentes = new Set(base.map((item) => String(item.value)));
  const nuevos = extra.filter((item) => !existentes.has(String(item.value)));
  return [...base, ...nuevos];
};

export default function useMatriculasOptions() {
  const [ciclosOptions, setCiclosOptions] = useState([]);
  const [promocionesOptions, setPromocionesOptions] = useState([]);
  const [cursosOptions, setCursosOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Promociones "finalizado": solo para matricular estudiantes rezagados de un corte de
  // migración, con permiso matriculas.excepcion_migracion (o root). Se cargan bajo demanda y se
  // mantienen separadas de las listas base para no afectar edición ni transferencia.
  const [ciclosFinalizadasOptions, setCiclosFinalizadasOptions] = useState([]);
  const [promocionesFinalizadasOptions, setPromocionesFinalizadasOptions] = useState([]);
  const [finalizadasCargadas, setFinalizadasCargadas] = useState(false);
  const [loadingFinalizadas, setLoadingFinalizadas] = useState(false);
  const [incluirFinalizadas, setIncluirFinalizadas] = useState(false);

  const loadOptions = useCallback(async () => {
    setLoadingOptions(true);
    try {
      const [promociones, cursos] = await Promise.all([
        listarPromocionesDisponibles(),
        listarCursos(),
      ]);

      // Las variantes comerciales (ej. Reentrenamiento N1/N2) nunca se reservan directamente:
      // se reserva sobre el curso oficial y la variante se elige aparte (select "Variante/Subtipo").
      const cursoOpts = (Array.isArray(cursos) ? cursos : [])
        .filter((item) => !item.curso_oficial_id)
        .map((item) => ({
          value: item.id,
          label: [item.nombre, item.centro].filter(Boolean).join(" | ") || `Curso #${item.id}`,
        }));

      setCiclosOptions(buildCiclosOptions(promociones));
      setPromocionesOptions(buildPromocionesOptions(promociones));
      setCursosOptions(cursoOpts);
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: err.message || "No se pudieron cargar promociones disponibles." });
    } finally {
      setLoadingOptions(false);
    }
  }, []);

  const loadFinalizadas = useCallback(async () => {
    if (finalizadasCargadas || loadingFinalizadas) return;
    setLoadingFinalizadas(true);
    try {
      const finalizadas = await listarPromocionesFinalizadas();
      setCiclosFinalizadasOptions(buildCiclosOptions(finalizadas));
      setPromocionesFinalizadasOptions(buildPromocionesOptions(finalizadas, { marcarFinalizada: true }));
      setFinalizadasCargadas(true);
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: err.message || "No se pudieron cargar promociones finalizadas." });
    } finally {
      setLoadingFinalizadas(false);
    }
  }, [finalizadasCargadas, loadingFinalizadas]);

  const toggleIncluirFinalizadas = useCallback(
    (value) => {
      setIncluirFinalizadas(value);
      if (value) loadFinalizadas();
    },
    [loadFinalizadas]
  );

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  // Listas exclusivas para el formulario de "Nueva matricula": incluyen finalizadas solo
  // cuando el usuario activa el toggle. ciclosOptions/promocionesOptions (base) no cambian,
  // por lo que edicion y transferencia de matricula siguen viendo solo lo disponible/en curso.
  const ciclosOptionsMatriculaNueva = useMemo(
    () => (incluirFinalizadas ? mergeById(ciclosOptions, ciclosFinalizadasOptions) : ciclosOptions),
    [ciclosOptions, ciclosFinalizadasOptions, incluirFinalizadas]
  );
  const promocionesOptionsMatriculaNueva = useMemo(
    () => (incluirFinalizadas ? mergeById(promocionesOptions, promocionesFinalizadasOptions) : promocionesOptions),
    [promocionesOptions, promocionesFinalizadasOptions, incluirFinalizadas]
  );

  return useMemo(
    () => ({
      ciclosOptions,
      promocionesOptions,
      cursosOptions,
      loadingOptions,
      reloadOptions: loadOptions,
      ciclosOptionsMatriculaNueva,
      promocionesOptionsMatriculaNueva,
      incluirFinalizadas,
      toggleIncluirFinalizadas,
      loadingFinalizadas,
    }),
    [
      ciclosOptions,
      promocionesOptions,
      cursosOptions,
      loadingOptions,
      loadOptions,
      ciclosOptionsMatriculaNueva,
      promocionesOptionsMatriculaNueva,
      incluirFinalizadas,
      toggleIncluirFinalizadas,
      loadingFinalizadas,
    ]
  );
}
